'use client';

import { type Context, createContext, type ReactNode, use } from 'react';

export interface DemoModeState {
  /**
   * True when rendering inside the `/demo-mode/*` route tree. Demo-mode is a
   * read-only marketing surface that lets visitors explore the app with
   * canned data; production routes are not in demo mode.
   */
  isDemoMode: boolean;
}

const DemoModeContext: Context<DemoModeState | null> = createContext<DemoModeState | null>(null);

/**
 * Read the demo-mode state. Returns `{ isDemoMode: false }` when no
 * `<DemoModeProvider>` is mounted above — production routes don't need to
 * mount the provider explicitly.
 */
export function useDemoMode(): DemoModeState {
  const ctx = use(DemoModeContext);
  return ctx ?? { isDemoMode: false };
}

export interface DemoModeProviderProps {
  isDemoMode: boolean;
  children: ReactNode;
}

/**
 * Provider that lifts the demo-mode flag out of layout/page prop drilling.
 * Mount once near the route boundary that should be considered demo-mode
 * (e.g. `app/[locale]/demo-mode/[[...slug]]/layout.tsx`).
 */
export function DemoModeProvider({ isDemoMode, children }: DemoModeProviderProps): React.ReactNode {
  return <DemoModeContext.Provider value={{ isDemoMode }}>{children}</DemoModeContext.Provider>;
}
